// Hair strands: a glTF LINES primitive rendered as screen-space ribbons with a
// hair shading model.
//
// The Unreal plugin publishes a MetaHuman groom's curves into the rig GLB as
// one LINES primitive per groom (positions, a TEXCOORD_0 of (root-to-tip,
// per-strand random), uint32 segment indices), placed under the head joint next
// to the groom's card mesh. GLTFLoader hands that back as a THREE.LineSegments
// with a LineBasicMaterial: one-pixel GL lines with no lighting, which reads as
// a wireframe, not hair. `upgradeHairStrands` swaps each such object for a
// LineSegments2 (three's instanced "fat lines": one quad per segment, width in
// pixels, antialiased edges through alpha-to-coverage) wearing a
// HairStrandMaterial. Nothing else about the object changes: name, userData
// (the material-slot stamp the binding walk keys on), parent and transform are
// kept, so the mapping row for the slot binds exactly as it does for a mesh.
//
// The shading is Kajiya-Kay's strand model plus the fake multiple scattering
// Unreal's own hair shader uses (KajiyaKayDiffuseAttenuation, HairBsdf.ush): a
// diffuse term on sqrt(albedo) around a "fake normal" facing the viewer, and a
// specular lobe that peaks where the strand's tangent bisects light and view.
// It reads the scene's directional lights through three's light uniforms
// (`lights: true`), so the same key light that shapes the skin shapes the hair;
// the IBL a ShaderMaterial cannot sample is stood in for by a flat fill term the
// row exposes as a parameter.
//
// A strand's material is not the graph material the row compiles to — a
// MeshPhysicalMaterial has no meaning on a line — so `applyGraphParamsToStrands`
// reads the row's exposed parameters by NAME (Hair Color, Roughness Factor,
// Specular Factor, Fill Light) into the strand material's uniforms. The row
// stays the single source of truth; live edits to it can drive the strands the
// same way they drive a mesh.
//
// The strands are SKINNED to the face (hairStrandSkin.js). The exporter parents
// a groom to the head joint as one rigid node — Unreal itself binds the groom
// to the skinned face mesh at runtime, so a beard there follows the jaw and a
// beard here, rigid under the skull, did not. Each strand's root is projected
// onto the nearest skinned vertex of the rig and takes its heaviest joint
// influences, and the ribbons render through three's own skinning path: the
// object is flagged `isSkinnedMesh` with the face's skeleton, the segments
// carry instanced `skinIndex` / `skinWeight`, and the vertex shader skins both
// ends of a segment with one bone blend (the whole strand rides its root). The
// object STAYS under the head joint, so the density volume and the mapping slot
// are what they were; `bindMatrixInverse` is refreshed per frame from the
// object's own world matrix (three's attached bind mode, done by hand), which is
// what makes a bone-parented skinned object come out right.

import * as THREE from 'three'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'
import { buildHairDensity } from './hairDensity.js'
import { strandRuns, VertexGrid, topInfluences, bindStrandRoots, STRAND_SKIN_INFLUENCES } from './hairStrandSkin.js'
// The gather is shared with the garment importer (skinTargets.js): both bind
// foreign geometry onto the rig's own skin and must see the same vertices.
import { collectSkinTargets, boneSkinMatrix, skinToWorldMatrix } from './skinTargets.js'

/** Shading models the strand material can run; 'unreal' is the shipped one. */
export const STRAND_MODELS = Object.freeze({ unreal: 0, scheuermann: 1 })

/**
 * Minimum drawn ribbon width in device pixels. A strand is drawn at least this
 * wide so it can be rasterised, and its COVERAGE (physical width / drawn
 * width) becomes the alpha the stochastic coverage test uses: a 0.3 px hair is
 * a 1 px ribbon that survives on 30% of its pixels, which is how a mass of
 * fibres shows gaps and the darker strands behind them.
 */
export const STRAND_MIN_WIDTH_PX = 1.0
/** Physical strand diameter in metres (a human hair is 50 to 100 microns; Unreal's groom default is 100). */
export const STRAND_DIAMETER_M = 0.0001

/** Parameter names the strand material reads off a bound row (the server's hair-strands preset). */
export const STRAND_PARAMS = Object.freeze({
  color: 'Hair Color',
  roughness: 'Roughness Factor',
  specular: 'Specular Factor',
  fill: 'Fill Light',
})

// True for the object GLTFLoader builds for a LINES primitive: a LineSegments
// with positions and the (u, seed) TEXCOORD_0 the plugin writes. A LINES
// primitive without that parameter is somebody else's wireframe and is left alone.
export function isStrandPrimitive(object) {
  return !!(object && object.isLineSegments && object.geometry?.attributes?.position && object.geometry.attributes.uv)
}

/**
 * Pure: turns an indexed LINES geometry (pairs of point indices) into the
 * per-SEGMENT arrays LineSegmentsGeometry wants — start xyz, end xyz — plus a
 * per-segment (uStart, uEnd, seed) parameter, and the strand structure
 * (hairStrandSkin.strandRuns: which strand each segment belongs to, and each
 * strand's root point). A non-indexed geometry is read as consecutive point
 * pairs. Returns { positions, params, segments, segmentStrand, roots }.
 */
export function segmentBuffers(geometry) {
  const pos = geometry.attributes.position
  const uv = geometry.attributes.uv
  const index = geometry.index
  const count = index ? index.count : pos.count
  const segments = Math.floor(count / 2)
  const positions = new Float32Array(segments * 6)
  const params = new Float32Array(segments * 4)
  for (let s = 0; s < segments; s++) {
    const a = index ? index.getX(s * 2) : s * 2
    const b = index ? index.getX(s * 2 + 1) : s * 2 + 1
    positions[s * 6 + 0] = pos.getX(a); positions[s * 6 + 1] = pos.getY(a); positions[s * 6 + 2] = pos.getZ(a)
    positions[s * 6 + 3] = pos.getX(b); positions[s * 6 + 4] = pos.getY(b); positions[s * 6 + 5] = pos.getZ(b)
    params[s * 4 + 0] = uv ? uv.getX(a) : 0
    params[s * 4 + 1] = uv ? uv.getX(b) : 0
    params[s * 4 + 2] = uv ? uv.getY(a) : 0.5
    params[s * 4 + 3] = 0
  }
  const { segmentStrand, roots } = strandRuns(index ? index.array : null, segments)
  return { positions, params, segments, segmentStrand, roots }
}

// `skinning_pars_vertex` is three's own chunk (bindMatrix, bindMatrixInverse,
// boneTexture, getBoneMatrix), guarded by USE_SKINNING — which three defines on
// the program when the OBJECT is flagged isSkinnedMesh, and with it declares
// the skinIndex / skinWeight attributes the geometry carries per instance.
const VERTEX_DECLS = `
attribute vec4 instanceParam;
uniform float hairDiameter;
uniform float hairCoverageScale;
varying vec3 vHairT;
varying vec3 vHairP;
varying vec2 vHairUS;
varying float vHairCoverage;
#include <skinning_pars_vertex>
`
// Replaces the stock shader's two endpoint transforms. A skinned set blends the
// four bone matrices ONCE per segment (both ends of a segment belong to one
// strand and carry its root's weights) and composes the result into the
// model-view transform: object -> bind space -> posed world -> object (through
// the per-frame bindMatrixInverse) -> view. Unskinned sets take modelViewMatrix
// unchanged, so a groom on a rig with no skinned mesh renders as before.
const VERTEX_START = `
#ifdef USE_SKINNING
	#include <skinbase_vertex>
	mat4 aosSkin = skinWeight.x * boneMatX + skinWeight.y * boneMatY + skinWeight.z * boneMatZ + skinWeight.w * boneMatW;
	mat4 aosStrandModelView = modelViewMatrix * bindMatrixInverse * aosSkin * bindMatrix;
#else
	mat4 aosStrandModelView = modelViewMatrix;
#endif
vec4 start = aosStrandModelView * vec4( instanceStart, 1.0 );
`
const VERTEX_END = `
vec4 end = aosStrandModelView * vec4( instanceEnd, 1.0 );
`
// Injected right after LineMaterial computes the segment's two view-space
// endpoints: the tangent is their difference, the shaded point is whichever
// end this quad vertex belongs to (position.y < 0.5 is the start, as the stock
// shader itself tests), and the strand parameter follows the same choice.
const VERTEX_MAIN = `
vHairT = normalize( end.xyz - start.xyz );
vHairP = ( position.y < 0.5 ? start : end ).xyz;
vHairUS = vec2( position.y < 0.5 ? instanceParam.x : instanceParam.y, instanceParam.z );
`
// A strand narrows toward its tip: the ribbon's screen width follows the
// root-to-tip parameter, so tips end in a point instead of a square cut. A
// per-strand factor keeps neighbouring hairs from being the same width.
// Physical width projected to the screen: pixels per metre at this depth is
// (resolution.y / 2) * projectionMatrix[1][1] / depth. The ribbon is drawn at
// max(that, linewidth) pixels and carries the ratio as its coverage.
const VERTEX_WIDTH = `
float aosTaper = mix( 1.0, 0.2, smoothstep( 0.5, 1.0, vHairUS.x ) ) * mix( 0.8, 1.2, instanceParam.z );
float aosDepth = max( -( position.y < 0.5 ? start.z : end.z ), 1e-4 );
float aosWidthPx = hairDiameter * aosTaper * 0.5 * resolution.y * projectionMatrix[ 1 ][ 1 ] / aosDepth;
float aosDrawnPx = max( aosWidthPx, linewidth );
vHairCoverage = min( aosWidthPx / aosDrawnPx * hairCoverageScale, 1.0 );
offset *= aosDrawnPx;
`
const FRAGMENT_DECLS = `
uniform vec3 hairColor;
uniform float hairRoughness;
uniform float hairSpecular;
uniform float hairFill;
uniform int hairModel;
uniform float hairTilt;
uniform sampler3D hairDensityMap;
uniform mat4 hairViewToLocal;
uniform vec3 hairDensityMin;
uniform vec3 hairDensityInvSize;
uniform float hairDensityStep;
uniform float hairDensityShadow;
uniform float hairDensityAO;
varying vec3 vHairT;
varying vec3 vHairP;
varying vec2 vHairUS;
varying float vHairCoverage;
#include <lights_pars_begin>

// Self-shadowing and occlusion from the hair's own voxel density
// (hairDensity.js), the stand-in for a deep-opacity map. The fragment position
// and the lights are in view space; hairViewToLocal takes both into the space
// the volume was built in. hairDensityShadow <= 0 disables both terms, so a
// material nobody configures shades exactly as before.
float aosDensityAt( vec3 pl ) {
	vec3 uvw = ( pl - hairDensityMin ) * hairDensityInvSize;
	if ( any( lessThan( uvw, vec3( 0.0 ) ) ) || any( greaterThan( uvw, vec3( 1.0 ) ) ) ) return 0.0;
	return texture( hairDensityMap, uvw ).r;
}

// Transmittance toward a light: eight steps of one voxel through the field.
float aosVolumeShadow( vec3 P, vec3 L ) {
	if ( hairDensityShadow <= 0.0 ) return 1.0;
	vec3 pl = ( hairViewToLocal * vec4( P, 1.0 ) ).xyz;
	vec3 dl = normalize( mat3( hairViewToLocal ) * L );
	// A per-pixel offset on the march start turns the voxel steps into noise
	// instead of bands; the strand mass averages it away.
	float jitter = fract( sin( dot( gl_FragCoord.xy, vec2( 12.9898, 78.233 ) ) ) * 43758.5453 );
	float acc = 0.0;
	for ( int i = 1; i <= 8; i++ ) {
		acc += aosDensityAt( pl + dl * ( hairDensityStep * ( float( i ) - jitter ) ) );
	}
	return exp( -hairDensityShadow * acc );
}

// Ambient occlusion: how dense the hair is where the point sits.
float aosVolumeOcclusion( vec3 P ) {
	if ( hairDensityShadow <= 0.0 ) return 1.0;
	vec3 pl = ( hairViewToLocal * vec4( P, 1.0 ) ).xyz;
	return exp( -hairDensityAO * aosDensityAt( pl ) );
}

// Scheuermann (ATI 2004, the model Three-Hair's HairStrandShader implements):
// two Kajiya-Kay lobes around tangents shifted along the fake normal, with a
// half-vector form and a smoothstep to kill the backfacing lobe.
float aosScheuermannLobe( vec3 T, vec3 V, vec3 L, float ex ) {
	vec3 H = normalize( L + V );
	float TdotH = dot( T, H );
	float sinTH = sqrt( max( 1.0 - TdotH * TdotH, 0.0 ) );
	float dirAtten = smoothstep( -1.0, 0.0, TdotH );
	return dirAtten * pow( sinTH, ex );
}

vec3 aosHairShadeScheuermann() {
	vec3 T = normalize( vHairT );
	vec3 V = normalize( -vHairP );
	vec3 albedo = hairColor * mix( 0.7, 1.3, vHairUS.y );
	float TdotV = dot( T, V );
	vec3 N = normalize( V - T * TdotV );
	float ex1 = exp2( 4.0 + 6.0 * ( 1.0 - clamp( hairRoughness, 0.0, 1.0 ) ) );
	vec3 col = albedo * ( ambientLightColor + vec3( hairFill ) ) * aosVolumeOcclusion( vHairP );
	#if NUM_DIR_LIGHTS > 0
	for ( int i = 0; i < NUM_DIR_LIGHTS; i++ ) {
		vec3 L = directionalLights[ i ].direction;
		vec3 Lc = directionalLights[ i ].color * aosVolumeShadow( vHairP, L );
		float NoL = clamp( mix( 0.25, 1.0, dot( N, L ) ), 0.0, 1.0 );
		vec3 T1 = normalize( T + N * hairTilt );
		vec3 T2 = normalize( T - N * hairTilt * 0.5 );
		float s1 = aosScheuermannLobe( T1, V, L, ex1 );
		float s2 = aosScheuermannLobe( T2, V, L, ex1 * 0.35 );
		vec3 specular = hairSpecular * ( s1 * vec3( 1.0 ) + s2 * mix( vec3( 1.0 ), albedo * 8.0, 0.6 ) );
		col += Lc * ( albedo * NoL * RECIPROCAL_PI + specular );
	}
	#endif
	return col;
}

vec3 aosHairShade() {
	if ( hairModel == 1 ) return aosHairShadeScheuermann();
	vec3 T = normalize( vHairT );
	vec3 V = normalize( -vHairP );
	// Per-strand tone from the seed, the way neighbouring hairs differ. Depth
	// comes from the density-volume terms above, the stand-in for a deep-opacity map.
	vec3 albedo = hairColor * mix( 0.5, 1.5, vHairUS.y );
	float TdotV = dot( T, V );
	float sinTV = sqrt( max( 1.0 - TdotV * TdotV, 0.0 ) );
	// Unreal's fake normal for the strand: the view direction bent onto the plane
	// perpendicular to the tangent, so a strand has a side that faces the camera.
	vec3 N = normalize( V - T * TdotV );
	// Sky/ambient: the flat fill plus any hemisphere light (sky above, ground
	// below, evaluated on the fake normal), occluded by the hair's own density.
	vec3 ambient = ambientLightColor + vec3( hairFill );
	#if NUM_HEMI_LIGHTS > 0
	for ( int i = 0; i < NUM_HEMI_LIGHTS; i++ ) {
		ambient += getHemisphereLightIrradiance( hemisphereLights[ i ], N );
	}
	#endif
	vec3 col = albedo * ambient * aosVolumeOcclusion( vHairP );
	// Per-strand jitter of the highlight shift and of the roughness (Marschner's
	// cuticle-tilt variation): without it the lobe is one continuous sheen band
	// across the mass; with it, it breaks into streaks on individual strands.
	float seed2 = fract( vHairUS.y * 7.13 + 0.37 );
	float tilt = hairTilt * mix( 0.4, 1.6, vHairUS.y );
	float rough = clamp( hairRoughness * mix( 0.7, 1.3, seed2 ), 0.0, 1.0 );
	float specPower = exp2( 4.0 + 5.0 * ( 1.0 - rough ) );
	#if NUM_DIR_LIGHTS > 0
	for ( int i = 0; i < NUM_DIR_LIGHTS; i++ ) {
		vec3 L = directionalLights[ i ].direction;
		vec3 Lc = directionalLights[ i ].color * aosVolumeShadow( vHairP, L );
		float TdotL = dot( T, L );
		float sinTL = sqrt( max( 1.0 - TdotL * TdotL, 0.0 ) );
		// Kajiya-Kay diffuse, mixed with a wrapped Lambert on the fake normal
		// (HairBsdf.ush, KajiyaKayDiffuseAttenuation): sqrt(albedo) is the
		// multiple-scattering stand-in, and 1/PI keeps it on Lambert's scale.
		float NoL = clamp( ( dot( N, L ) + 1.0 ) * 0.25, 0.0, 1.0 );
		vec3 scatter = sqrt( albedo ) * mix( NoL, sinTL, 0.33 ) * RECIPROCAL_PI;
		// Kajiya-Kay specular: cos of the angle between L and V measured around the
		// strand, raised to a power set by the roughness; a second lobe shifted along
		// the fake normal is the coloured secondary highlight real hair shows.
		// Primary lobe (R): white, shifted toward the root along the fake normal.
		vec3 T1 = normalize( T - N * tilt );
		float T1dotL = dot( T1, L ); float T1dotV = dot( T1, V );
		float spec1 = pow( max( T1dotL * T1dotV + sqrt( max( 1.0 - T1dotL * T1dotL, 0.0 ) ) * sqrt( max( 1.0 - T1dotV * T1dotV, 0.0 ) ), 0.0 ), specPower );
		// Secondary lobe (TRT): coloured by the hair, broader, shifted the other way.
		vec3 T2 = normalize( T + N * tilt * 1.5 );
		float T2dotL = dot( T2, L ); float T2dotV = dot( T2, V );
		float spec2 = pow( max( T2dotL * T2dotV + sqrt( max( 1.0 - T2dotL * T2dotL, 0.0 ) ) * sqrt( max( 1.0 - T2dotV * T2dotV, 0.0 ) ), 0.0 ), specPower * 0.08 );
		vec3 specular = hairSpecular * ( spec1 * vec3( 1.0 ) + spec2 * 0.7 * mix( vec3( 0.15 ), sqrt( albedo ), 0.85 ) );
		// Transmission (TT): a strand lit from behind glows toward the viewer, tinted
		// by the hair — the rim light a backlit head of hair shows. Peaks when the
		// light is behind the strand relative to the eye.
		// Only a strand lying across the light/eye axis transmits — with no strand
		// term this glowed on every unshadowed fibre at once, a silver wash.
		float tt = pow( clamp( dot( -L, V ), 0.0, 1.0 ), 12.0 ) * sinTL * sinTV * 0.25;
		col += Lc * ( scatter + specular * clamp( sinTL, 0.0, 1.0 ) + tt * sqrt( albedo ) );
	}
	#endif
	return col;
}
`
// Replaces LineMaterial's final colour write: the shaded strand, and a tip that
// thins out over its last third instead of ending in a square cut.
// vUv.x runs -1..1 across the ribbon: fading the outer part of the width gives
// the strand a soft edge through alpha-to-coverage instead of a hard quad edge,
// which is what makes flyaways read as wisps rather than wire. The base width
// is set wider to compensate (STRAND_MIN_WIDTH_PX).
// Coverage below 1 is BLENDED: alpha = coverage, depth test against the opaque
// scene, no depth write between strands. Two order-independent routes were
// measured first and both fail on this hardware: alpha-to-coverage, because
// D3D maps an alpha level to one FIXED sample mask (no spatial dither), so
// every thin strand lands on the same sample and twenty layers still leave
// three of four samples showing the scalp — the mass renders as a veil; and a
// hashed per-pixel discard, which is correct but one binary decision per pixel,
// visibly grainy ("powdery specks" in review). Blending accumulates smoothly,
// 1-(1-a)^n over n layers, at the cost of draw-order errors between strands of
// nearly the same colour, which is the trade every real-time hair blender makes.
const FRAGMENT_OUT = `
float aosEdge = 1.0 - smoothstep( 0.2, 1.0, abs( vUv.x ) );
float aosAlpha = alpha * aosEdge * vHairCoverage;
if ( aosAlpha <= 0.002 ) discard;
gl_FragColor = vec4( aosHairShade(), aosAlpha );
`

/**
 * three's fat-line material with the hair shading spliced in. `lights: true`
 * makes three feed the scene's light uniforms; `alphaToCoverage` gives the
 * ribbon antialiased edges on an MSAA canvas. `resolution` has to track the
 * drawing buffer (LineMaterial measures width in pixels); see setStrandResolution.
 */
export class HairStrandMaterial extends LineMaterial {
  constructor(params = {}) {
    super({
      linewidth: STRAND_MIN_WIDTH_PX, worldUnits: false, alphaToCoverage: false,
      transparent: true, depthWrite: false, depthTest: true, ...params,
    })
    this.isHairStrandMaterial = true
    this.lights = true
    this.uniforms = THREE.UniformsUtils.merge([
      this.uniforms,
      THREE.UniformsLib.lights,
      {
        hairDiameter: { value: STRAND_DIAMETER_M },
        // A decimated strand set stands in for more hair than it has (Unreal's
        // groom LODs widen strands as they drop them); >1 scales the coverage.
        // 1 is the physical coverage (drawn width over true width). It sat at 2
        // while the exporter thinned Aoi's groom to every second strand; with the
        // whole groom present the doubling only over-darkened the 0.03 mm peach
        // fuzz into a stipple on the skin, and the crown measured the same either
        // way (46,30,23 against Unreal's 41,27,22).
        hairCoverageScale: { value: 1 },
        hairColor: { value: new THREE.Color(0.02, 0.01, 0.005) },
        hairRoughness: { value: 0.6 },
        hairSpecular: { value: 0.15 },
        hairFill: { value: 0.35 },
        hairModel: { value: 0 },
        hairTilt: { value: 0.15 },
        hairDensityMap: { value: null },
        hairViewToLocal: { value: new THREE.Matrix4() },
        hairDensityMin: { value: new THREE.Vector3() },
        hairDensityInvSize: { value: new THREE.Vector3(1, 1, 1) },
        hairDensityStep: { value: 0.01 },
        hairDensityShadow: { value: 0 },
        hairDensityAO: { value: 0 },
      },
    ])
    // UniformsUtils.merge clones; LineMaterial's own accessors (linewidth,
    // resolution, ...) go through this.uniforms, so they keep working.
    this.linewidth = STRAND_MIN_WIDTH_PX
    this.vertexShader = injectStrandVertex(this.vertexShader)
    this.fragmentShader = injectStrandFragment(this.fragmentShader)
    this.needsUpdate = true
  }
}

// Exported for the unit test: the splice points are stock LineMaterial lines,
// and a three upgrade that moves them must fail loudly here, not render lines
// silently unlit.
export function injectStrandVertex(vertexShader) {
  let out = mustReplace(vertexShader, 'attribute vec3 instanceEnd;', 'attribute vec3 instanceEnd;' + VERTEX_DECLS)
  out = mustReplace(out, 'vec4 start = modelViewMatrix * vec4( instanceStart, 1.0 );', VERTEX_START)
  out = mustReplace(out, 'vec4 end = modelViewMatrix * vec4( instanceEnd, 1.0 );', VERTEX_END + VERTEX_MAIN)
  // The screen-space branch's width write (the worldUnits branch keeps its own).
  out = mustReplace(out, 'offset *= linewidth;', VERTEX_WIDTH)
  return out
}

export function injectStrandFragment(fragmentShader) {
  // After the last `pars` include: `lights_pars_begin` needs `<common>`
  // (IncidentLight, pow2, inverseTransformDirection), which LineMaterial
  // includes AFTER its own uniforms, so splicing at `uniform float opacity;`
  // put the light chunk ahead of common and it failed to compile.
  let out = mustReplace(fragmentShader, '#include <clipping_planes_pars_fragment>', '#include <clipping_planes_pars_fragment>' + FRAGMENT_DECLS)
  out = mustReplace(out, 'gl_FragColor = vec4( diffuseColor.rgb, alpha );', FRAGMENT_OUT)
  return out
}

function mustReplace(source, anchor, replacement) {
  if (!source.includes(anchor)) {
    throw new Error(`hairStrands: LineMaterial shader has no "${anchor}" to splice at; three changed its fat-line shader`)
  }
  return source.replace(anchor, replacement)
}

/**
 * Swaps every strand primitive under root for a LineSegments2 wearing a
 * HairStrandMaterial, in place in the hierarchy. Returns the new objects.
 */
/** Voxels per axis of a strand set's density field; 96 measured fine for an 800k-point groom. */
export const STRAND_DENSITY_RES = 96
/**
 * The shadow / occlusion extinction the character path ships with. Measured on
 * Aoi's groom against the Unreal render of the same head (crown patch mean,
 * sRGB): specular 0.45 / shadow 1 / ao 1 gave 74,52,42 against Unreal's
 * 41,27,22 — a silver sheen, the white specular lobe summing through the
 * blended layers (lowering coverage made it BRIGHTER, 83,56,45, more layers
 * showing). Specular 0.15 with shadow 3 / ao 2.5 gave 54,35,27, the closest of
 * the six variants tried; the density shadow is the lever, since it is what
 * keeps the deep layers from adding their highlight.
 */
export const STRAND_DENSITY_LOOK = Object.freeze({ shadow: 3.0, ao: 2.5 })

/** Whether `HairStrandMaterial` has been ported to a node material.
 *
 *  A CONSTANT, not a flag: there is nothing a user or a URL could set, because
 *  flipping it on today would wedge the renderer rather than draw hair. It is
 *  named so the gate below reads as "not yet" and so the one thing that turns
 *  strands back on is greppable from either end. */
const STRANDS_PORTED = false

/**
 * The app's entry point, and today it is a GATE: the groom is left as the
 * exporter's CARDS mesh and the one-pixel wireframe GLTFLoader built is hidden.
 *
 * `HairStrandMaterial` is a `LineMaterial` — a `ShaderMaterial` — with ~200
 * lines of Kajiya-Kay, volume shadowing and per-strand skinning spliced into its
 * GLSL. A node renderer compiles none of it, and the failure is not a groom that
 * looks wrong: three throws inside `_renderObjectDirect` mid-frame and every
 * later render for every scene silently draws nothing. So this is a visible
 * downgrade for a MetaHuman groom, LABELLED rather than silent, because the
 * alternative is a black viewport with a clean console.
 *
 * `buildStrandRibbons` below is the rest — the weight transfer, the density
 * volume, the segment buffers — none of which is renderer-specific and all of
 * which is still right. It is kept, exported and TESTED as the port's starting
 * point; a `Line2NodeMaterial` (three ships the class) is what reconnects it,
 * and TODO.md tracks that.
 */
export function upgradeHairStrands(root, { log = (m) => console.info(m) } = {}) {
  const found = []
  root.traverse(o => { if (isStrandPrimitive(o)) found.push(o) })
  if (found.length === 0) return []
  if (STRANDS_PORTED) return buildStrandRibbons(root, { log })
  log(`[hairStrands] ${found.length} strand set(s) left as CARDS: the spliced `
    + `Kajiya-Kay LineMaterial does not compile on a node renderer. `
    + `Porting it to Line2NodeMaterial is what turns this back on.`)
  for (const line of found) line.visible = false
  return []
}

/**
 * The upgrade itself: swap each LINES primitive for a `LineSegments2` wearing
 * `HairStrandMaterial`, skinned to the rig at each strand's root.
 *
 * NOT REACHED FROM THE APP while `STRANDS_PORTED` is false — it is exported so
 * its behaviour stays pinned rather than rotting behind a gate. Driving it
 * directly is what the unit tests do, deliberately: a test that went through the
 * gate would assert the cards path and prove nothing about this.
 */
export function buildStrandRibbons(root, { log = (m) => console.info(m) } = {}) {
  const upgraded = []
  const found = []
  root.traverse(o => { if (isStrandPrimitive(o)) found.push(o) })
  if (found.length === 0) return upgraded
  // World matrices are needed twice below (root projection, bind matrices) and
  // the loader leaves them for the first render; the skinned meshes' bind
  // matrices were taken in this same frame, so the two agree.
  root.updateMatrixWorld(true)
  const targets = collectSkinTargets(root, { skip: (o) => o.userData?.isHairStrands || o.userData?.isGarment })
  for (const line of found) {
    const bufs = segmentBuffers(line.geometry)
    const { positions, params, segments } = bufs
    if (segments === 0) continue
    const geometry = new LineSegmentsGeometry()
    geometry.setPositions(positions)
    geometry.setAttribute('instanceParam', new THREE.InstancedBufferAttribute(params, 4))
    const material = new HairStrandMaterial()
    // What the exporter said about this groom: its melanin colour rides the glTF
    // material (GLTFLoader hands it back on a LineBasicMaterial) and its strand
    // width the mesh extras (metres). Absent, the material's defaults stand.
    if (line.material?.color?.isColor) material.uniforms.hairColor.value.copy(line.material.color)
    const width = Number(line.userData?.hairWidth)
    if (Number.isFinite(width) && width > 0) material.uniforms.hairDiameter.value = width
    const ribbons = new LineSegments2(geometry, material)
    ribbons.name = line.name
    ribbons.userData = { ...line.userData, isHairStrands: true, strandSegments: segments }
    ribbons.position.copy(line.position)
    ribbons.quaternion.copy(line.quaternion)
    ribbons.scale.copy(line.scale)
    ribbons.frustumCulled = false
    // A depth-only pass would draw the base quad, not the instances.
    ribbons.castShadow = false
    ribbons.receiveShadow = false
    // Self-shadowing and occlusion from the strands' own voxel density, in the
    // object's space (hairDensity.js); the per-frame view matrix comes from
    // updateStrandDensityFrame.
    attachStrandDensity(material, buildHairDensity(positions, { res: STRAND_DENSITY_RES }), STRAND_DENSITY_LOOK)
    // Ride the skin at the root (hairStrandSkin.js): a rig with no skinned mesh
    // leaves the set rigid under its parent, as the exporter placed it.
    const binding = skinStrandsToRig(ribbons, line, bufs, targets)
    if (binding) {
      const mm = (v) => `${(v * 1000).toFixed(1)} mm`
      log(`[hairStrands] ${line.name}: ${binding.strands} strands skinned to ${binding.skin} (root to skin: median ${mm(binding.medianDist)}, p95 ${mm(binding.p95Dist)}` +
        (binding.unbound ? `, ${binding.unbound} too far, left rigid on ${binding.fallback}` : '') +
        (binding.restBindDeviation > 1e-3 ? `; NOTE rest pose is ${binding.restBindDeviation.toExponential(1)} off the bind pose` : '') + ')')
    }
    const parent = line.parent
    if (parent) {
      const at = parent.children.indexOf(line)
      parent.remove(line)
      parent.add(ribbons)
      if (at >= 0) { parent.children.splice(parent.children.indexOf(ribbons), 1); parent.children.splice(at, 0, ribbons) }
    }
    line.geometry.dispose()
    if (line.material?.dispose) line.material.dispose()
    // The strands REPLACE the groom's card mesh: the exporter places
    // `<Groom>_strands` beside `<Groom>_cards`, and drawing both puts opaque
    // card sheets through the strand mass. The card mesh is hidden, not
    // removed, so it stays in the outliner and the mapping.
    const cardsName = line.name.replace(/_strands$/, '_cards')
    const cards = cardsName !== line.name && parent ? parent.children.find(c => c.isMesh && c.name === cardsName) : null
    if (cards) {
      cards.visible = false
      cards.userData.hiddenByStrands = line.name
      ribbons.userData.replacesCards = cardsName
    }
    upgraded.push(ribbons)
  }
  return upgraded
}

const _v = new THREE.Vector3()
const _m = new THREE.Matrix4()
const _m2 = new THREE.Matrix4()

/**
 * Binds a strand set to the skeleton whose skin its roots sit on, and turns the
 * ribbons into a skinned object of that skeleton. Returns the binding report,
 * or null when the rig has no skinned mesh to bind to.
 */
function skinStrandsToRig(ribbons, line, bufs, targets) {
  if (targets.length === 0 || bufs.roots.length === 0) return null
  const pos = line.geometry.attributes.position
  // Ribbon local -> world -> SKIN space, the space collectSkinTargets indexes
  // the body in. The skin->world map is the skeleton's own (skinToWorldMatrix),
  // NOT a body mesh's `matrixWorld`: on a rig whose meshes hang under a scaled
  // node the two differ by that scale, and the roots would then be projected
  // onto a body of the wrong size. Every target of a rig shares one skeleton
  // frame at the bind pose, so the first target's is every target's.
  // Per target, because two skeletons of one rig need not share a frame (a
  // 875-bone hair rig is not the body's), so the roots are re-projected for
  // whichever skin is being scored.
  const roots = new Float32Array(bufs.roots.length * 3)
  const toSkin = new THREE.Matrix4()
  const projectRootsInto = (target) => {
    toSkin.multiplyMatrices(skinToWorldMatrix(target.skeleton, _m).invert(), line.matrixWorld)
    for (let i = 0; i < bufs.roots.length; i++) {
      _v.fromBufferAttribute(pos, bufs.roots[i]).applyMatrix4(toSkin)
      roots[i * 3] = _v.x; roots[i * 3 + 1] = _v.y; roots[i * 3 + 2] = _v.z
    }
  }
  // The skeleton whose skin the roots are nearest, judged on a sample: a beard
  // and the scalp both belong to the face, but a rig can carry several skins.
  let best = null, bestScore = Infinity
  const step = Math.max(1, Math.floor(bufs.roots.length / 256))
  for (const t of targets) {
    projectRootsInto(t)
    let sum = 0, n = 0
    for (let i = 0; i < bufs.roots.length; i += step, n++) {
      t.grid.nearest(roots[i * 3], roots[i * 3 + 1], roots[i * 3 + 2])
      sum += Math.min(t.grid.lastDist, 0.1)
    }
    const score = sum / n
    if (score < bestScore) { bestScore = score; best = t }
  }
  const skeleton = best.skeleton
  // Leave `roots` and `toSkin` in the CHOSEN skin's space: the loop above left
  // them in the last one scored, which is the winner only by accident.
  projectRootsInto(best)
  // A root the skin does not reach stays rigid on the joint the exporter parented
  // the set to; when that joint is not in this skeleton, on the one nearest the roots.
  let fallbackBone = skeleton.bones.indexOf(line.parent)
  if (fallbackBone < 0) {
    _v.set(0, 0, 0)
    for (let i = 0; i < bufs.roots.length; i++) { _v.x += roots[i * 3]; _v.y += roots[i * 3 + 1]; _v.z += roots[i * 3 + 2] }
    _v.divideScalar(bufs.roots.length)
    let bd = Infinity
    skeleton.bones.forEach((b, i) => { const d = boneSkinMatrix(skeleton, i, _m).elements; const dd = (d[12] - _v.x) ** 2 + (d[13] - _v.y) ** 2 + (d[14] - _v.z) ** 2; if (dd < bd) { bd = dd; fallbackBone = i } })
  }
  const bound = bindStrandRoots({
    roots, segmentStrand: bufs.segmentStrand, grid: best.grid, fallbackBone,
    influencesAt: (v, oi, ow, o) => topInfluences(best.sets[best.owner[v]], best.local[v], STRAND_SKIN_INFLUENCES, oi, ow, o),
  })
  const geometry = ribbons.geometry
  geometry.setAttribute('skinIndex', new THREE.InstancedBufferAttribute(bound.skinIndex, 4))
  geometry.setAttribute('skinWeight', new THREE.InstancedBufferAttribute(bound.skinWeight, 4))
  // bindMatrix takes ribbon space into the skeleton's SKIN space — the same
  // `toSkin` the roots were projected through above, so the ribbons deform in
  // the space they were bound in. Derived from the SKELETON rather than from a
  // body mesh's `matrixWorld`, which is only the same thing while that mesh's
  // node is the identity (see skinTargets.js).
  const ref = best.meshes[0]
  ribbons.isSkinnedMesh = true
  ribbons.skeleton = skeleton
  ribbons.bindMatrix = new THREE.Matrix4().copy(toSkin)
  ribbons.bindMatrixInverse = new THREE.Matrix4().copy(line.matrixWorld).invert()
  // How far the rig's current pose is from its bind pose: the projection above
  // was made at the current pose, so a rig loaded mid-animation would bind the
  // roots to the wrong skin. Every bone's (world * inverse-bind) equals the same
  // frame delta at the bind pose; report the largest departure from bone 0's.
  let restBindDeviation = 0
  if (skeleton.bones.length > 1) {
    _m2.copy(skeleton.bones[0].matrixWorld).multiply(skeleton.boneInverses[0])
    for (let i = 1; i < skeleton.bones.length; i++) {
      _m.copy(skeleton.bones[i].matrixWorld).multiply(skeleton.boneInverses[i])
      for (let e = 0; e < 16; e++) { const d = Math.abs(_m.elements[e] - _m2.elements[e]); if (d > restBindDeviation) restBindDeviation = d }
    }
  }
  const report = {
    ...bound.stats,
    skin: ref.name || `skeleton of ${skeleton.bones.length} bones`,
    skinMeshes: best.meshes.map((m) => m.name),
    fallback: skeleton.bones[fallbackBone]?.name ?? String(fallbackBone),
    restBindDeviation,
  }
  ribbons.userData.strandBinding = report
  return report
}

/**
 * Reads the bound row's parameters into the strand material. `graphMaterial` is
 * the compiled graph material for the row (buildGraphMaterial); its
 * userData.graphParams is keyed `${nodeId}:${handle}` with `{ name, value }`.
 * Returns the names it applied, for the report; an absent parameter keeps the
 * material's default rather than zeroing it.
 */
export function applyGraphParamsToStrands(strandMaterial, graphMaterial) {
  const applied = []
  const params = graphMaterial?.userData?.graphParams ?? {}
  const byName = new Map()
  for (const p of Object.values(params)) {
    if (p && p.name) byName.set(p.name, p)
  }
  const u = strandMaterial.uniforms
  const color = byName.get(STRAND_PARAMS.color)
  if (color && Array.isArray(color.value) && color.value.length >= 3) {
    u.hairColor.value.setRGB(color.value[0], color.value[1], color.value[2])
    applied.push(STRAND_PARAMS.color)
  }
  for (const [key, uniform] of [[STRAND_PARAMS.roughness, 'hairRoughness'], [STRAND_PARAMS.specular, 'hairSpecular'], [STRAND_PARAMS.fill, 'hairFill']]) {
    const p = byName.get(key)
    if (p && typeof p.value === 'number' && Number.isFinite(p.value)) {
      u[uniform].value = p.value
      applied.push(key)
    }
  }
  return applied
}

/**
 * Sets the strand look in one call: { widthPx (min drawn), diameter (metres), color:[r,g,b], roughness,
 * specular, fill, model:'unreal'|'scheuermann', tilt }. Unknown/undefined
 * fields are left alone. Returns the material for chaining.
 */
export function setStrandLook(material, look = {}) {
  const u = material.uniforms
  if (Number.isFinite(look.widthPx)) material.linewidth = look.widthPx
  if (Number.isFinite(look.diameter)) u.hairDiameter.value = look.diameter
  if (Number.isFinite(look.coverageScale)) u.hairCoverageScale.value = look.coverageScale
  if (Array.isArray(look.color) && look.color.length >= 3) u.hairColor.value.setRGB(look.color[0], look.color[1], look.color[2])
  if (Number.isFinite(look.roughness)) u.hairRoughness.value = look.roughness
  if (Number.isFinite(look.specular)) u.hairSpecular.value = look.specular
  if (Number.isFinite(look.fill)) u.hairFill.value = look.fill
  if (Number.isFinite(look.tilt)) u.hairTilt.value = look.tilt
  if (Number.isFinite(look.shadow)) u.hairDensityShadow.value = look.shadow
  if (Number.isFinite(look.ao)) u.hairDensityAO.value = look.ao
  if (look.model !== undefined) {
    if (!(look.model in STRAND_MODELS)) throw new Error(`hairStrands: unknown shading model "${look.model}"`)
    u.hairModel.value = STRAND_MODELS[look.model]
  }
  return material
}

const _viewToLocal = new THREE.Matrix4()

/**
 * Attaches a voxel density field (hairDensity.js) to the material as a 3D
 * texture and sets the field's frame. Returns the texture so the caller can
 * dispose it. `shadow` and `ao` are the extinction scales of the two terms;
 * shadow 0 disables both.
 */
export function attachStrandDensity(material, field, { shadow = 0.6, ao = 1.2 } = {}) {
  const tex = new THREE.Data3DTexture(field.data, field.res, field.res, field.res)
  tex.format = THREE.RedFormat
  tex.type = THREE.UnsignedByteType
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  tex.wrapS = tex.wrapT = tex.wrapR = THREE.ClampToEdgeWrapping
  tex.unpackAlignment = 1
  tex.needsUpdate = true
  const u = material.uniforms
  if (u.hairDensityMap.value) u.hairDensityMap.value.dispose()
  u.hairDensityMap.value = tex
  u.hairDensityMin.value.fromArray(field.min)
  u.hairDensityInvSize.value.set(1 / field.size[0], 1 / field.size[1], 1 / field.size[2])
  u.hairDensityStep.value = field.voxel
  u.hairDensityShadow.value = shadow
  u.hairDensityAO.value = ao
  material.needsUpdate = true
  return tex
}

/**
 * Per-frame: the view -> volume-local transform, from the strand object's world
 * matrix and the camera; and, for a skinned set, the bind-matrix inverse three's
 * attached bind mode keeps for a SkinnedMesh — the object sits under an animated
 * joint, so the skinned world position has to come back through its CURRENT
 * world matrix, not the one it was bound with. Allocation-free.
 */
export function updateStrandDensityFrame(strandObject, camera) {
  const u = strandObject.material.uniforms
  _viewToLocal.copy(strandObject.matrixWorld).invert()
  if (strandObject.isSkinnedMesh) strandObject.bindMatrixInverse.copy(_viewToLocal)
  _viewToLocal.multiply(camera.matrixWorld)
  u.hairViewToLocal.value.copy(_viewToLocal)
}

const _size = new THREE.Vector2()

/**
 * LineMaterial measures width in drawing-buffer pixels and needs the buffer
 * size as a uniform. Called from the render loop with the upgraded objects and
 * the renderer; allocation-free, and writes only when the size changed.
 */
export function setStrandResolution(strands, renderer) {
  renderer.getDrawingBufferSize(_size)
  for (let i = 0; i < strands.length; i++) {
    const m = strands[i].material
    if (!m || !m.isHairStrandMaterial) continue
    const r = m.resolution
    if (r.x !== _size.x || r.y !== _size.y) r.set(_size.x, _size.y)
  }
}
